import { CallsFiltersBar } from './CallsFilters';
import { EmptyPageDex, ErrorPageDex, PoolRow } from '../shared/Common';
import { useCallsSearchFilters } from './usePageParams';
import { GetNewCallsParams } from 'api/types/httpsTypes/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';
import { duration } from 'moment';
import Header from 'modules/shared-components/data-display/table/header';
import Table from 'modules/shared-components/data-display/table';
import { desktopColumns, mobileColumns } from './columns';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { thDynamicClass } from '../SnipingOpportunitiesCard';
import { useRef } from 'react';
import { Pagination } from 'modules/shared-components/pagination';
import { TableColumn } from 'modules/shared-components/data-display/table/types';
import { useGlobalChain } from 'modules/routing/chainProvider';
import { FastBuyWrapper } from '../../../terminal/components/templates/FastBuyWrapper';

export function TelegramCalls() {
  return (
    <FastBuyWrapper>
      <TelegramCallsBody />
    </FastBuyWrapper>
  );
}

export function TelegramCallsBody() {
  const { goToPage, filtersState, setSort } = useCallsSearchFilters();
  const [selectedChain] = useGlobalChain();
  const params: GetNewCallsParams = {
    status: filtersState.status,
    channelId: filtersState.channelId,
    limit: filtersState.limit,
    offset: filtersState.offset,
    chain: selectedChain,
    sort: filtersState.sort,
    minLiquidity: filtersState.minLiquidity,
    maxLiquidity: filtersState.maxLiquidity,
    minMarketcap: filtersState.minMarketcap,
    maxMarketcap: filtersState.maxMarketcap,
  };

  const {
    data: page,
    error: error,
    isLoading,
  } = useQuery({
    queryKey: contractServiceKeys.getNewCalls(params),
    queryFn: () => ContractService.getNewCalls(params),
    keepPreviousData: true,
    refetchInterval: duration(12, 'seconds').asMilliseconds(),
  });
  const isLargeScreen = useMediaQuery(screenGte.large);
  const columns = isLargeScreen ? desktopColumns : mobileColumns;

  const listRef = useRef<HTMLTableElement>(null);

  function scrollAndNavigate(pageNumber: number) {
    goToPage(pageNumber);
    if (listRef.current) {
      listRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  const isSortAscending = !filtersState.sort?.includes('-');

  return (
    <div className="space-y-2 lg:px-2">
      <div className="xxs:px-1 lg:px-0">
        <CallsFiltersBar />
      </div>

      {error ? (
        <ErrorPageDex />
      ) : (
        <>
          <div className="overflow-x-auto">
            <div className="w-full  xxs:my-3 lg:min-w-[1200px] ">
              {!isLargeScreen && <div ref={listRef} />}
              <Table className="xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6">
                <Header
                  trClassName="lg:grid lg:grid-cols-9 lg:dark:bg-dex-black-900  border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-b-dex-black-700"
                  columns={columns as TableColumn<any>[]}
                  thDynamicClass={thDynamicClass}
                  rowClassName="xxs:p-1"
                  isSortAscending={isSortAscending}
                  sortBy={filtersState.sort?.replace('-', '')}
                  className="xxs:dark:bg-dex-black-900 xxs:text-xs lg:grid"
                  onClickColumn={(_, sortableKey) => {
                    if (sortableKey && !isLoading) setSort(sortableKey);
                  }}
                />
                <tbody className="lg:block lg:h-terminal overflow-y-auto">
                  {isLoading ? (
                    [...Array(10).keys()]
                      .map((i) => i + 1)
                      .map((index) => (
                        <Table.LoadingRow
                          thDynamicClass={thDynamicClass}
                          key={index}
                          elementClassName="xxs:dark:bg-dex-black-700"
                          className="lg:grid lg:grid-cols-9 lg:dark:bg-dex-black-900"
                          columns={columns as TableColumn<any>[]}
                        />
                      ))
                  ) : page?.data?.length === 0 ? (
                    <EmptyPageDex
                      height={isLargeScreen ? '100px' : '50vh'}
                      title="No Telegram Calls Found"
                    />
                  ) : (
                    page?.data?.map((item) => (
                      <PoolRow
                        filtersState={filtersState}
                        key={item.tokenAddress}
                        data={item}
                        columnsDef={columns}
                      />
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          {page && page?.data?.length > 0 && (
            <div className="xxs:px-2 lg:px-0">
              <Pagination
                goToPage={scrollAndNavigate}
                activePage={page.pagination}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
